import {observer} from "mobx-react";
import _ from "lodash";
import {NiceModalForm} from "../../components/Modals";
import {Form, message, Spin, Tabs} from "antd";
import Leaderboard from "../tournament/Leaderboard";
import React, {useEffect, useState} from "react";
import PlayoffSettingsFields from "./PlayoffSettingsFields";
import {useForm} from "antd/es/form/Form";
import NiceModal from "@ebay/nice-modal-react";
import {callAndReload, callWithCatch, pp} from "../../utils";
import XhrBtn from "../../components/XhrBtn";
import GroupsSelector from "../../components/GroupsSelector";
import {KnockoutFlex} from "../tournament/KnockOut";
import {useEffectOnce} from "react-use";
import Tourney from "@/apps/tourney/Tourney.js";
import {HolderOutlined} from "@ant-design/icons";

const GenPlayoff = NiceModal.create(observer(({d, teamById}) => {
  const [form] = useForm();
  const [activeTab, setActiveTab] = useState("bracket");
  const [tempGroups, setTempGroups] = useState([]);
  const [bracket, setBracket] = useState(null);
  const [lanes, setLanes] = useState();
  const [loading, setLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  
  let prelims = _.reject(d.pools, 'knockout');
  let teams = tempGroups && tempGroups[0];

  useEffectOnce(() => { window.scrollTo(0, 0); });

  const loadPlayoffSchedule = (team_ids, playoffSettings = {}) => {
    setLoading(true);
    const formValues = form.getFieldsValue();
    const settings = { ...playoffSettings, ...formValues };
    
    callWithCatch({
      url: `/divisions/${d.id}/playoff_simulation`,
      params: { 
        team_ids,
        bracket: settings.playoff_style || bracket,
        take_to_playoff: settings.take_to_playoff,
        playoff_style: settings.playoff_style,
        playoff_format: settings.playoff_format,
        rounds_format: settings.rounds_format,
        with_consolation: settings.with_consolation,
        take_to_consolation: settings.take_to_consolation
      },
      method: 'get',
      onSuccess: data => {
        setTempGroups([data.teams.map(((t, i) => ({...t, name: `${i+1}. ${t.name}`})))]);
        setLanes(data.lanes);
        setBracket(data.bracket_format);
        
        const formValues = {
          take_to_playoff: data.take_to_playoff?.toString(),
          playoff_style: data.playoff_style,
          playoff_format: data.playoff_format,
          rounds_format: data.rounds_format,
          with_playoff: data.with_playoff,
          with_consolation: data.with_consolation,
          take_to_consolation: data.take_to_consolation
        };
        
        form.setFieldsValue(formValues);
        
        // Update playoff pool if it exists
        if (data.playoff_pool) {
          d.pools = d.pools.map(pool => 
            pool.knockout ? { ...pool, ...data.playoff_pool } : pool
          );
        }
        
        setLoading(false);
        setIsFirstLoad(false);
      },
      onError: () => {
        setLoading(false);
        setIsFirstLoad(false);
      }
    });
  };

  const onDrop = items => {
    setTempGroups(_.cloneDeep(tempGroups));
    loadPlayoffSchedule(tempGroups[0].map(t => t.id));
  };

  const onSave = async (vals) => {
    await callAndReload({
      url: '/divisions/' + d.id,
      params: { gen_playoff: true, groups: tempGroups.map(g => g.map(t => t.id)), settings: vals },
      reload: true,
      onSuccess: () => message.success("Playoff Generated"),
      method: 'put',
    });
  };

  // Load playoff schedule when modal opens
  useEffect(() => {
    loadPlayoffSchedule();
  }, []);

  // Watch for form changes and reload bracket when settings change
  const takeToPlayoff = Form.useWatch('take_to_playoff', form);
  const playoffStyle = Form.useWatch('playoff_style', form);
  const playoffFormat = Form.useWatch('playoff_format', form);
  const roundsFormat = Form.useWatch('rounds_format', form);
  const withConsolation = Form.useWatch('with_consolation', form);
  const takeToConsolation = Form.useWatch('take_to_consolation', form);

  useEffect(() => {
    if (lanes && teams) {
      loadPlayoffSchedule(teams.map(t => t.id));
    }
  }, [takeToPlayoff, playoffStyle, playoffFormat, roundsFormat, withConsolation, takeToConsolation]);

  // Only enable save button when on bracket tab
  // const saveButtonEnabled = activeTab === "bracket";
  const saveButtonEnabled = true;

  const renderBracketTab = () => {
    // First time loading - show full spinner
    if (isFirstLoad && (loading || !lanes)) {
      return (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      );
    }

    const showPoolNumbers = prelims.length > 1;

    // Subsequent loads - show overlay spinner over existing content
    const content = (
      <div className="flex flex-row gap-8">
        <div className="space-y-4">
          <GroupsSelector
            groups={tempGroups}
            onDrop={onDrop}
            name="Seed Order"
            itemRender={(item) => {
              const poolColors = ['text-sky-500', 'text-purple-500', 'text-emerald-500', 'text-orange-500'];
              const poolColor = showPoolNumbers && poolColors[item.pool_num % poolColors.length];

              return (
                <div style={{width: "225px"}} className="cursor-grab shadow p-2 m-1 bg-white relative">
                  <div className="flex items-start gap-2">
                    <HolderOutlined className="mt-1" />
                    <div className="flex-1">
                      <div>{item.name}</div>
                      <div className="text-slate-500">{item.captain_name}</div>
                    </div>
                  </div>
                  {showPoolNumbers && <div className={`absolute top-1 right-1 text-xs font-semibold ${poolColor} p-1 rounded`}>
                    P{item.pool_num}
                  </div> }
                </div>
              );
            }}
          />
        </div>
        <div className="bg-white overflow-x-auto" style={{width: "450px"}}>
          <KnockoutFlex lanes={lanes} />
        </div>
      </div>
    );

    if (loading && !isFirstLoad) {
      return (
        <Spin spinning={true} size="large">
          {content}
        </Spin>
      );
    }

    return content;
  };

  return (
    <NiceModalForm 
      title="Generate Playoff" 
      style={{top: 0}} 
      footer={null} 
      width={900}
      form={form}
      onSave={onSave}
      okText="Generate Playoff"
      okButtonProps={{ disabled: !saveButtonEnabled }}
      initialValues={d}
    >
      <Tabs
        activeKey={activeTab} 
        onChange={setActiveTab}
        tabBarExtraContent={
          <XhrBtn 
            onClick={() => onSave(form.getFieldsValue())} 
            type="primary" 
            disabled={!saveButtonEnabled}
          >
            Generate Playoff
          </XhrBtn>
        }
      >
        {prelims.map(dp =>
          <Tabs.TabPane tab={dp.name} key={dp.id}>
            <Leaderboard division={dp} teamById={teamById}/>
          </Tabs.TabPane>
        )}

        { !Tourney.joola && <Tabs.TabPane tab="Playoff Settings" key="settings" forceRender>
          <PlayoffSettingsFields form={form} availableTeams={teams || []} />
        </Tabs.TabPane> }
        
        <Tabs.TabPane tab="Playoff Bracket" key="bracket">
          {renderBracketTab()}
        </Tabs.TabPane>
      </Tabs>
    </NiceModalForm>
  );
}));

export default GenPlayoff;